/**
 * Health module — single sensitive record per child (GDPR Art. 9). View + edit
 * via PUT. Editable fields mirror UpdateHealthRecordInput.
 */
import { useEffect, useState } from 'react';
import type { HealthRecord, UpdateHealthRecordInput } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import {
  Badge,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';

type EditableField = keyof UpdateHealthRecordInput;

const SINGLE_LINE: EditableField[] = ['insurance', 'bloodType'];

export function HealthModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const childId = activeChild!.id;

  const { data, loading, error, reload } = useAsync<HealthRecord>(
    () => api.getHealthRecord(family.id, childId),
    [family.id, childId],
  );

  const [editing, setEditing] = useState(false);

  return (
    <section>
      <SectionHeader
        title={t.health.title}
        subtitle={
          <span className="row" style={{ gap: 8 }}>
            <Badge tone="accent">{t.health.sensitive}</Badge>
            <span>{t.health.lead}</span>
          </span>
        }
        action={
          data && !editing ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setEditing(true)}
            >
              {t.common.edit}
            </button>
          ) : null
        }
      />

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}

      {data && !editing ? (
        <div className="card stack">
          <HealthRow label={t.health.allergies} value={data.allergies} />
          <HealthRow label={t.health.medications} value={data.medications} />
          <HealthRow label={t.health.insurance} value={data.insurance} />
          <HealthRow label={t.health.bloodType} value={data.bloodType} />
          <HealthRow label={t.health.conditions} value={data.conditions} />
          <HealthRow label={t.health.notes} value={data.notes} />
          <p className="text-xs muted" style={{ marginTop: 8 }}>
            {t.health.updatedAt}: {new Date(data.updatedAt).toLocaleString('sk-SK')}
          </p>
        </div>
      ) : null}

      {data && editing ? (
        <HealthEditForm
          record={data}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function HealthRow({ label, value }: { label: string; value: string | null }) {
  const { t } = useI18n();
  return (
    <div className="stack-sm">
      <span className="field-label">{label}</span>
      <span className={value ? '' : 'muted text-sm'}>
        {value || t.health.placeholderNone}
      </span>
    </div>
  );
}

const FIELDS: Array<{ key: EditableField; labelKey: keyof typeof FIELD_LABELS }> = [
  { key: 'allergies', labelKey: 'allergies' },
  { key: 'medications', labelKey: 'medications' },
  { key: 'insurance', labelKey: 'insurance' },
  { key: 'bloodType', labelKey: 'bloodType' },
  { key: 'conditions', labelKey: 'conditions' },
  { key: 'notes', labelKey: 'notes' },
];

// Indirection so labels resolve via the dictionary at render time.
const FIELD_LABELS = {
  allergies: 'allergies',
  medications: 'medications',
  insurance: 'insurance',
  bloodType: 'bloodType',
  conditions: 'conditions',
  notes: 'notes',
} as const;

function HealthEditForm({
  record,
  onCancel,
  onSaved,
}: {
  record: HealthRecord;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const [values, setValues] = useState<Record<EditableField, string>>({
    allergies: record.allergies ?? '',
    medications: record.medications ?? '',
    insurance: record.insurance ?? '',
    bloodType: record.bloodType ?? '',
    conditions: record.conditions ?? '',
    notes: record.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setValues({
      allergies: record.allergies ?? '',
      medications: record.medications ?? '',
      insurance: record.insurance ?? '',
      bloodType: record.bloodType ?? '',
      conditions: record.conditions ?? '',
      notes: record.notes ?? '',
    });
  }, [record]);

  async function submit() {
    setBusy(true);
    setErr(null);
    const input: UpdateHealthRecordInput = {
      allergies: values.allergies.trim() || null,
      medications: values.medications.trim() || null,
      insurance: values.insurance.trim() || null,
      bloodType: values.bloodType.trim() || null,
      conditions: values.conditions.trim() || null,
      notes: values.notes.trim() || null,
    };
    try {
      await api.updateHealthRecord(family.id, activeChild!.id, input);
      onSaved();
    } catch {
      setErr(t.errors.generic);
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <Form onSubmit={submit}>
        {err ? <ErrorState message={err} /> : null}
        {FIELDS.map(({ key, labelKey }) => (
          <Field key={key} label={t.health[labelKey]}>
            {({ id }) =>
              SINGLE_LINE.includes(key) ? (
                <input
                  id={id}
                  className="input"
                  value={values[key]}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [key]: e.target.value }))
                  }
                />
              ) : (
                <textarea
                  id={id}
                  className="textarea"
                  value={values[key]}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [key]: e.target.value }))
                  }
                />
              )
            }
          </Field>
        ))}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.save}
          </button>
        </div>
      </Form>
    </div>
  );
}
